import { ALERT_THRESHOLD_MAX, PAGE_SIZE_OPTIONS, PROPERTY_ALERT_FLOOR } from '@common/enums';
import {
  DEFAULT_ACCOUNT_NOTIFICATION_PREFERENCE,
  DEFAULT_NOTIFICATION_DELIVERY,
  DEFAULT_USER_SETTINGS,
} from '../data/user-settings.data';
import type {
  AccountNotificationPreference,
  AppearanceSettings,
  EventNotificationPreference,
  LandingPage,
  NotificationEventKey,
  PersonalChannel,
  RespondingSettings,
  ReviewEmailMode,
  ScopeSelection,
  StartupDateRangePreset,
  StartupMode,
  StoredNotificationPreferences,
  SummaryKind,
  TicketSettings,
  UserNotificationDelivery,
  UserSettings,
  WorkspaceSettings,
} from '../types';

const LANDING_PAGES: ReadonlyArray<LandingPage> = [
  'dashboard',
  'pinned-dashboard',
  'reviews',
  'tickets',
  'guests',
  'reports',
];

const STARTUP_PRESETS: ReadonlyArray<StartupDateRangePreset> = [
  'today',
  'yesterday',
  'last7',
  'last30',
  'last90',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastYear',
  'last365',
];

const REVIEW_EMAIL_MODES: ReadonlyArray<ReviewEmailMode> = ['all', 'alerts', 'off'];
const SUMMARY_KINDS: ReadonlyArray<SummaryKind> = ['daily', 'weekly', 'monthly'];
const PERSONAL_CHANNELS: ReadonlyArray<PersonalChannel> = ['in-app', 'email'];
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Repairs a stored settings document against the current schema. Anything missing or malformed
 * falls back to its default field by field, so one bad value never costs the user the rest.
 */
export function reconcileUserSettings(raw: unknown): UserSettings {
  const obj = asRecord(raw);
  return {
    version: 1,
    workspace: reconcileWorkspace(obj['workspace']),
    appearance: reconcileAppearance(obj['appearance']),
    responding: reconcileResponding(obj['responding']),
    tickets: reconcileTickets(obj['tickets']),
    updatedAt: typeof obj['updatedAt'] === 'string' ? obj['updatedAt'] : null,
  };
}

function reconcileWorkspace(raw: unknown): WorkspaceSettings {
  const obj = asRecord(raw);
  const fallback = DEFAULT_USER_SETTINGS.workspace;
  return {
    landingPage: oneOf(obj['landingPage'], LANDING_PAGES, fallback.landingPage),
    landingDashboardId:
      typeof obj['landingDashboardId'] === 'string' && obj['landingDashboardId']
        ? obj['landingDashboardId']
        : null,
    startupScope: startupMode(obj['startupScope'], fallback.startupScope),
    startupScopeSelection: reconcileSelection(obj['startupScopeSelection']),
    startupDateRange: startupMode(obj['startupDateRange'], fallback.startupDateRange),
    startupDateRangePreset: oneOf(
      obj['startupDateRangePreset'],
      STARTUP_PRESETS,
      fallback.startupDateRangePreset,
    ),
  };
}

function reconcileAppearance(raw: unknown): AppearanceSettings {
  const obj = asRecord(raw);
  const fallback = DEFAULT_USER_SETTINGS.appearance;
  return {
    density: obj['density'] === 'compact' ? 'compact' : fallback.density,
    pageSize: oneOf(obj['pageSize'], PAGE_SIZE_OPTIONS, fallback.pageSize),
    rememberSidebar: obj['rememberSidebar'] === true,
  };
}

function reconcileResponding(raw: unknown): RespondingSettings {
  const obj = asRecord(raw);
  return {
    signature: typeof obj['signature'] === 'string' ? obj['signature'] : null,
    defaultTone:
      typeof obj['defaultTone'] === 'string' && obj['defaultTone'] ? obj['defaultTone'] : null,
  };
}

function reconcileTickets(raw: unknown): TicketSettings {
  const obj = asRecord(raw);
  const fallback = DEFAULT_USER_SETTINGS.tickets;
  return {
    autoWatchCreated:
      typeof obj['autoWatchCreated'] === 'boolean'
        ? obj['autoWatchCreated']
        : fallback.autoWatchCreated,
    autoWatchCommented:
      typeof obj['autoWatchCommented'] === 'boolean'
        ? obj['autoWatchCommented']
        : fallback.autoWatchCommented,
    defaultListScope: obj['defaultListScope'] === 'mine' ? 'mine' : 'all',
  };
}

function reconcileSelection(raw: unknown): ScopeSelection {
  const obj = asRecord(raw);
  const kind = obj['kind'];
  const id = typeof obj['id'] === 'string' ? obj['id'] : '';
  if ((kind === 'view' || kind === 'tag' || kind === 'account') && id) return { kind, id };
  return { kind: 'all' };
}

export function reconcileNotificationPreferences(
  raw: unknown,
  userId: string,
): StoredNotificationPreferences {
  const obj = asRecord(raw);
  const accounts = Array.isArray(obj['accounts'])
    ? obj['accounts']
        .map((row) => reconcileAccountPreference(row, userId))
        .filter((row): row is AccountNotificationPreference => row !== null)
    : [];
  const events = Array.isArray(obj['events'])
    ? obj['events']
        .map((row) => reconcileEventPreference(row, userId))
        .filter((row): row is EventNotificationPreference => row !== null)
    : [];
  return {
    accounts,
    events,
    delivery: obj['delivery'] ? reconcileDelivery(obj['delivery'], userId) : null,
  };
}

function reconcileAccountPreference(
  raw: unknown,
  userId: string,
): AccountNotificationPreference | null {
  const obj = asRecord(raw);
  const accountId = obj['accountId'];
  if (typeof accountId !== 'string' || !accountId) return null;
  const fallback = DEFAULT_ACCOUNT_NOTIFICATION_PREFERENCE;
  const threshold = Number(obj['alertThreshold']);
  return {
    userId,
    accountId,
    reviewEmails: oneOf(obj['reviewEmails'], REVIEW_EMAIL_MODES, fallback.reviewEmails),
    summaries: Array.isArray(obj['summaries'])
      ? SUMMARY_KINDS.filter((kind) => (obj['summaries'] as unknown[]).includes(kind))
      : [...fallback.summaries],
    alertThreshold: Number.isInteger(threshold)
      ? Math.min(ALERT_THRESHOLD_MAX, Math.max(PROPERTY_ALERT_FLOOR, threshold))
      : fallback.alertThreshold,
  };
}

function reconcileEventPreference(
  raw: unknown,
  userId: string,
): EventNotificationPreference | null {
  const obj = asRecord(raw);
  const event = obj['event'];
  if (typeof event !== 'string' || !event) return null;
  const channels = Array.isArray(obj['channels'])
    ? PERSONAL_CHANNELS.filter((channel) => (obj['channels'] as unknown[]).includes(channel))
    : [];
  return { userId, event: event as NotificationEventKey, channels };
}

function reconcileDelivery(raw: unknown, userId: string): UserNotificationDelivery {
  const obj = asRecord(raw);
  const fallback = DEFAULT_NOTIFICATION_DELIVERY;
  return {
    userId,
    combineAccounts: obj['combineAccounts'] === true,
    summaryContent: obj['summaryContent'] === 'full' ? 'full' : 'scores',
    summaryTime: time(obj['summaryTime'], fallback.summaryTime),
    quietHoursEnabled: obj['quietHoursEnabled'] === true,
    quietHoursFrom: time(obj['quietHoursFrom'], fallback.quietHoursFrom),
    quietHoursTo: time(obj['quietHoursTo'], fallback.quietHoursTo),
  };
}

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

function oneOf<T>(value: unknown, allowed: ReadonlyArray<T>, fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function startupMode(value: unknown, fallback: StartupMode): StartupMode {
  return value === 'fixed' || value === 'last-used' ? value : fallback;
}

function time(value: unknown, fallback: string): string {
  return typeof value === 'string' && TIME_RE.test(value) ? value : fallback;
}
